import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Terminal } from "lucide-react";

export default function Login() {
    const { user, login } = useAuth();
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    if (user) return <Navigate to="/" replace />;

    async function submit(e) {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        const r = await login(email, password);
        setSubmitting(false);
        if (r.ok) nav("/");
        else setError(r.error || "Login failed");
    }

    return (
        <div className="min-h-screen grid lg:grid-cols-2">
            <div className="hidden lg:flex relative grid-skeleton bg-[hsl(240,3%,4%)] items-center p-16">
                <div className="relative z-10 max-w-md">
                    <div className="text-[11px] uppercase tracking-[0.25em] text-primary font-mono mb-4">
                        OMX // Support Operations
                    </div>
                    <h1 className="text-5xl font-bold leading-tight tracking-tight mb-4">
                        Every ticket. Every minute. One console.
                    </h1>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                        WhatsApp-first support desk with live SLA countdowns,
                        auto-escalations and a full mock stack for demos.
                    </p>
                    <div className="mt-10 grid grid-cols-3 gap-3">
                        {["IMMEDIATE", "PRIORITY", "STANDARD"].map((t, i) => (
                            <div
                                key={t}
                                className="border border-border bg-[hsl(240,3%,8%)] rounded-md p-3"
                            >
                                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-mono">
                                    Tier {"ABC"[i]}
                                </div>
                                <div className="text-sm mt-1">{t}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <div className="flex items-center justify-center p-8">
                <form
                    onSubmit={submit}
                    className="w-full max-w-sm space-y-5"
                    data-testid="login-form"
                >
                    <div className="flex items-center gap-2 mb-8">
                        <div className="size-8 rounded-md bg-primary flex items-center justify-center text-primary-foreground">
                            <Terminal className="size-4" />
                        </div>
                        <div className="text-sm tracking-wide">OMX Console</div>
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wider text-muted-foreground">
                            Email
                        </label>
                        <input
                            data-testid="login-email-input"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="mt-1 w-full bg-card border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                            required
                        />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wider text-muted-foreground">
                            Password
                        </label>
                        <input
                            data-testid="login-password-input"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="mt-1 w-full bg-card border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                            required
                        />
                    </div>
                    {error && (
                        <div
                            data-testid="login-error"
                            className="text-xs text-red-400 border border-red-500/30 bg-red-500/10 px-3 py-2 rounded-md"
                        >
                            {error}
                        </div>
                    )}
                    <button
                        data-testid="login-submit-btn"
                        disabled={submitting}
                        className="w-full bg-primary text-primary-foreground font-medium py-2 rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50"
                    >
                        {submitting ? "Signing in…" : "Sign in"}
                    </button>
                    <div className="text-[11px] text-muted-foreground text-center pt-4">
                        Demo credentials are pre-filled. Agents:{" "}
                        <span className="font-mono">agent1-5@omxdigital.com / Agent@123</span>
                    </div>
                </form>
            </div>
        </div>
    );
}
